import { ErrorInputTypes } from "@shared/features/tradingTerminal/common/types";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import {
  ErrorTypes,
  InputOrderData,
  OrderSectionErrors,
  OrderTypes,
  UpdateOrder,
} from "@shared/features/trades/common/types";
import { ORDER_KEY_TO_ERROR_TYPES } from "@shared/features/tradingTerminal/common/constants";
import {
  calculateDistanceFromPrice,
  getPriceValidationMessage,
} from "@shared/features/tradingTerminal/common/utils";
import {
  getAmountValidationMessage,
  getErrorDisplayMessage,
} from "@shared/features/trades/common/utils";
import { toFixed } from "@shared/utils/numbers";
import { useContext, useMemo, useState } from "react";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

export interface UseOrderProps {
  order: InputOrderData;
  index: number;
  updateOrder?: UpdateOrder;
  orderType: OrderTypes;
  amount: number;
  orderSectionErrors?: OrderSectionErrors;
  baselineEntryPrice?: number;
}

const useOrder = ({
  order,
  index,
  updateOrder,
  orderType,
  amount,
  orderSectionErrors,
  baselineEntryPrice,
}: UseOrderProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const { t: tTrades } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const [focusedInputType, setFocusedInputType] = useState<
    ErrorInputTypes | undefined
  >();
  const {
    availableAmount,
    getMinAmountByPrice,
    setPriceTargetsModified,
    setStrategyValuesModified,
    currentPrice,
    setValuesModified,
    useTradingTerminalOldVersion,
    precisionConfig,
    account,
  } = useContext(TradingTerminalContext)!;
  const { getExchangePriceFactorLimits } = useContext(
    ExchangesMetadataContext
  )!;
  const priceFactorLimits = getExchangePriceFactorLimits(account?.exchange);

  const percentPrecision = precisionConfig?.percent || 0;
  const orderErrors = orderSectionErrors?.orders[index] || {};
  const sectionErrors = orderSectionErrors?.section;

  const onBlur = () => setFocusedInputType(undefined);

  const getFocusedInputError = () => {
    let inputError: ErrorTypes | undefined = undefined;
    if (focusedInputType && focusedInputType in ORDER_KEY_TO_ERROR_TYPES) {
      for (let errorType of ORDER_KEY_TO_ERROR_TYPES[focusedInputType]) {
        if (errorType in orderErrors) {
          inputError = errorType as unknown as ErrorTypes;
          break;
        }
      }
    }
    return inputError;
  };

  const getOrderKeysWithErrors = () => {
    let inputsWithErrors = new Set<string>();
    Object.keys(orderErrors).forEach((error) => {
      Object.entries(ORDER_KEY_TO_ERROR_TYPES).forEach(
        ([orderKey, errorTypes]) => {
          if (errorTypes.includes(parseInt(error) as unknown as ErrorTypes)) {
            inputsWithErrors.add(orderKey);
          }
        }
      );
    });
    if (ErrorTypes.SUM_OF_RATIOS in (sectionErrors || {})) {
      inputsWithErrors.add("percent");
    }
    return inputsWithErrors;
  };

  const onChange = (key: keyof InputOrderData, value: string) => {
    const updatedOrder = { ...order, [key]: value };
    if (key === "amount") {
      updatedOrder.percent = value
        ? ((parseFloat(value) / amount) * 100).toString()
        : "";
    } else if (key === "percent" && "amount" in updatedOrder) {
      updatedOrder.amount = value
        ? ((parseFloat(value) / 100) * amount).toString()
        : "";
    }
    if (
      orderType !== OrderTypes.ENTRY &&
      key === "price" &&
      "distance" in updatedOrder
    ) {
      updatedOrder.distance = baselineEntryPrice
        ? calculateDistanceFromPrice(
            parseFloat(value),
            baselineEntryPrice,
            percentPrecision
          ).toString()
        : "";
    }
    if (key === "price") setPriceTargetsModified(orderType, true);
    if (key === "percent" || key === "amount")
      setStrategyValuesModified(orderType, true);
    setValuesModified(true);
    updateOrder?.(index, orderType, updatedOrder);
  };

  let errorMessage: string | undefined = undefined;
  const focusedInputErrorType = useMemo(
    () => getFocusedInputError(),
    [focusedInputType, orderErrors]
  );
  const orderKeysWithErrors = useMemo(
    () => getOrderKeysWithErrors(),
    [orderErrors, sectionErrors]
  );

  if (
    focusedInputErrorType === ErrorTypes.INVALID_ORDER_RATIO ||
    (focusedInputErrorType === undefined && focusedInputType === "percent")
  ) {
    errorMessage = t("messages.errors.ratio_range");
  } else if (
    focusedInputErrorType === ErrorTypes.INVALID_ENTRY_ORDER_AMOUNT ||
    (focusedInputErrorType === undefined && focusedInputType === "amount")
  ) {
    const minAmount = getMinAmountByPrice(parseFloat(order.price));
    errorMessage = getAmountValidationMessage(minAmount, availableAmount);
  } else if (
    focusedInputErrorType === ErrorTypes.INVALID_ORDER_PRICE ||
    (focusedInputErrorType === undefined && focusedInputType === "price")
  ) {
    errorMessage = getPriceValidationMessage(
      t,
      currentPrice,
      precisionConfig?.price,
      priceFactorLimits
    );
  } else if (focusedInputErrorType !== undefined) {
    errorMessage = getErrorDisplayMessage(
      tTrades,
      focusedInputErrorType,
      orderType
    );
  }

  const getDistance = () => {
    if (currentPrice) {
      const diff = Math.abs(currentPrice - Number(order.price));
      return toFixed((diff / currentPrice) * 100, percentPrecision);
    }
  };

  const distanceDisplay =
    order.distance !== "" ? (order.distance || 0) + "%" : t("labels.n_a");
  const percentDisplay = `${toFixed(order.percent, percentPrecision)}%`;

  return {
    percentPrecision,
    setFocusedInputType,
    focusedInputErrorType,
    orderKeysWithErrors,
    distanceDisplay,
    percentDisplay,
    errorMessage,
    getDistance,
    onChange,
    onBlur,
    useTradingTerminalOldVersion,
    precisionConfig,
  };
};

export default useOrder;
